import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ARViewer.module.css';
import { useUserLocation } from '../../hooks/useUserLocation'; // Adjust path as needed
import { useCameraBackground } from '../../hooks/useCameraBackground';
import { useCameraConfiguration } from '../../hooks/useCameraConfiguration';

// eslint-disable-next-line @typescript-eslint/no-namespace
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'a-scene': any;
      'a-entity': any;
      'a-light': any;
      'a-camera': any;
      'a-box': any;
      'a-sky': any;
      'a-plane': any;
      'a-text': any;
      'a-videosphere': any;
    }
  }
  interface Window { AFRAME: any; THREE: any; }
}

/* eslint-enable @typescript-eslint/no-explicit-any */

// Register A-Frame component in module scope (runs when module loads)
if (typeof window !== 'undefined' && window.AFRAME) {
  if (!window.AFRAME.components['model-event-handler']) {
    window.AFRAME.registerComponent('model-event-handler', {
      schema: { placementData: { type: 'string' } },
      init: function (this: any) { // Use explicit 'this' type if possible, 'any' for simplicity here
        const el = this.el;
        const sceneEl = this.el.sceneEl;
        const data = this.data;
        this.modelLoadedListener = () => {
          // console.log('A-FRAME EVENT: model-loaded detected by component for', data.placementData);
          sceneEl.emit('react-model-loaded', { placement: data.placementData }, false);
        };
        el.addEventListener('model-loaded', this.modelLoadedListener);
      },
      remove: function (this: any) { // Use explicit 'this' type
        if (this.modelLoadedListener) {
          this.el.removeEventListener('model-loaded', this.modelLoadedListener);
        }
      }
    });
    // console.log('Registered A-Frame model-event-handler component in module scope');
  } else {
    // console.log('A-Frame model-event-handler component already registered');
  }
} else if (typeof window !== 'undefined' && !window.AFRAME) {
  // console.warn('AFRAME not found when attempting to register model-event-handler component.');
}

// Define a more specific type for placement data if possible
interface PlacementModelFile {
    id: number;
    name: string;
    alternativeText: string | null;
    caption: string | null;
    width: number | null;
    height: number | null;
    formats: any | null; // Define formats if known
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl: string | null;
    provider: string;
    provider_metadata: any | null; // Define metadata if known
    created_at: string;
    updated_at: string;
}

interface Placement {
    id: number;
    name?: string;
    latitude: number;
    longitude: number;
    scale?: string;
    rotation?: string;
    model_file?: PlacementModelFile[];
    created_at: string;
    updated_at: string;
    published_at?: string; // Optional based on your data
}

interface ARViewerProps {
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error | string) => void;
}

const getBackendBaseUrl = () => {
  if (import.meta.env.MODE === 'production') {
    return import.meta.env.VITE_APP_BACKEND_BASEURL_LIVE;
  }
  return import.meta.env.VITE_APP_backend_baseUrl_localTest;
};

// Helper functions for manual GPS calculations
const toRadians = (degrees: number): number => degrees * Math.PI / 180;
const toDegrees = (radians: number): number => radians * 180 / Math.PI;

const calculateHaversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δφ = toRadians(lat2 - lat1);
  const Δλ = toRadians(lon2 - lon1);
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in meters
};

const calculateBearing = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const φ1 = toRadians(lat1);
  const λ1 = toRadians(lon1);
  const φ2 = toRadians(lat2);
  const λ2 = toRadians(lon2);
  const y = Math.sin(λ2 - λ1) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) -
            Math.sin(φ1) * Math.cos(φ2) * Math.cos(λ2 - λ1);
  const θ = Math.atan2(y, x);
  return (toDegrees(θ) + 360) % 360; // Bearing in degrees from North
};

interface ModelTransform {
  position: string;
  rotation: string;
  scale: string;
}

// Helper to parse scale string "x y z" to numbers
const parseScaleString = (scaleStr: string | undefined, defaultScale = 1): { x: number; y: number; z: number } => {
  if (!scaleStr) return { x: defaultScale, y: defaultScale, z: defaultScale };
  const parts = scaleStr.split(' ').map(Number);
  return {
    x: parts[0] ?? defaultScale,
    y: parts[1] ?? defaultScale,
    z: parts[2] ?? defaultScale,
  };
};

// Helper to format scale numbers to string "x y z"
const formatScaleToString = (scale: { x: number; y: number; z: number }): string => {
  return `${scale.x.toFixed(3)} ${scale.y.toFixed(3)} ${scale.z.toFixed(3)}`;
};

export const ARViewer: React.FC<ARViewerProps> = ({
  onStart,
  onEnd,
  onError,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [nearbyPlacements, setNearbyPlacements] = useState<Placement[]>([]);
  const [modelsLoaded, setModelsLoaded] = useState(0);
  const [showDebugInfo, setShowDebugInfo] = useState(true);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | null>(null); // THIS IS CORRECTLY DECLARED
  const [modelWorldTransforms, setModelWorldTransforms] = useState<{ [id: number]: ModelTransform }>({});
  const [worldOriginGps, setWorldOriginGps] = useState<{ lat: number; lng: number } | null>(null); // New state for world origin
  const [anchorModelId, setAnchorModelId] = useState<number | null>(null); 
  
  const sceneRef = useRef<HTMLElement | null>(null);
  const cameraRef = useRef<HTMLElement | null>(null);

  const { userLocation, locationError } = useUserLocation();
  const { videoRef, videoError, streamActive } = useCameraBackground(true);
  useCameraConfiguration(cameraRef, isSceneReady);
  const MAX_AR_DISTANCE = 100;
  const MODEL_DEFAULT_Y = 1.6;

  // Constants for dynamic scaling
  const REFERENCE_DISTANCE_FOR_SCALE = 10;
  const MIN_SCALE_MULTIPLIER = 0.1;
  const MAX_SCALE_MULTIPLIER = 5.0;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('debug');
    const inDevEnvironment = import.meta.env.MODE === 'development';
    const debugEnabled = debugParam === 'true' || inDevEnvironment;
    
    setIsDebugMode(debugEnabled);
    // Control showDebugInfo based on the determined debug mode
    setShowDebugInfo(debugEnabled); 
  }, []);

  const handleModelError = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    console.error('Model loading error:', e, target?.getAttribute('gltf-model'));
    setError('Failed to load 3D model');
    // onError?.(new Error('Failed to load 3D model')); // Assuming onError prop exists
  }, [/*onError*/]);

  const handleModelLoaded = useCallback((placement: Placement) => {
    if (!placement || !placement.id) {
      return;
    }
    setModelsLoaded(prev => {
      const totalCurrent = nearbyPlacements.length || 0;
      const newCount = prev + 1;
      return Math.min(newCount, totalCurrent);
    });
  }, [nearbyPlacements.length]);

  useEffect(() => {
    if (!isSceneReady || !sceneRef.current) return;
    const sceneElement = sceneRef.current;
    const handleReactModelLoaded = (event: CustomEvent) => {
      try {
        const placementDataString = event?.detail?.placement;
        if (placementDataString && typeof placementDataString === 'string') {
          const eventPlacement: Placement = JSON.parse(placementDataString);
          if (eventPlacement && eventPlacement.id) {
            handleModelLoaded(eventPlacement);
          }
        }
      } catch (parseError) {
        console.error('Error parsing placement data from scene event:', parseError, event?.detail?.placement);
      }
    };
    sceneElement.addEventListener('react-model-loaded', handleReactModelLoaded as EventListener);
    return () => {
      if (sceneElement) {
          sceneElement.removeEventListener('react-model-loaded', handleReactModelLoaded as EventListener);
      }
    };
  }, [isSceneReady, handleModelLoaded]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement && streamActive) {
      const handleMetadata = () => {
        if (videoElement.videoHeight > 0) {
          setVideoAspectRatio(videoElement.videoWidth / videoElement.videoHeight);
        }
      };
      if (videoElement.readyState >= videoElement.HAVE_METADATA) handleMetadata();
      else videoElement.addEventListener('loadedmetadata', handleMetadata);
      return () => videoElement.removeEventListener('loadedmetadata', handleMetadata);
    } else {
      setVideoAspectRatio(null);
    }
  }, [videoRef, streamActive]);

  useEffect(() => {
    let mounted = true;
    const initAFrameAndFetch = async () => {
      try {
        while (typeof window === 'undefined' || !window.AFRAME || !window.AFRAME.components['model-event-handler']) {
          await new Promise(resolve => setTimeout(resolve, 500));
          if (!mounted) return;
        }
        setIsLoading(false); setIsSceneReady(true); setError(null);
        // onStart?.(); // Assuming onStart prop exists
        const res = await fetch(`${getBackendBaseUrl()}/ar-placements?_limit=100&_sort=created_at:desc`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        if (Array.isArray(data) && mounted) {
          setPlacements(data as Placement[]);
          setModelsLoaded(0); 
        } else if (mounted) {
          setPlacements([]);
        }
      } catch (err: any) {
        console.error('Failed to initialize A-Frame or fetch placements:', err);
        if (mounted) {
          setError('Failed to initialize A-Frame or fetch placements');
          // onError?.(err instanceof Error ? err : String(err));
        }
      }
    };
    initAFrameAndFetch();
    return () => { mounted = false; /* onEnd?.(); */ }; // Assuming onEnd prop exists
  }, [/*onStart, onEnd, onError*/]);

  // useEffect to calculate manual model placements (NOW ABSOLUTE WORLD POSITIONS)
  useEffect(() => {
    if (!userLocation || placements.length === 0) {
      setNearbyPlacements([]);
      // worldOriginGps persists once set, do not clear it here unless specifically intended
      setModelWorldTransforms({});
      setAnchorModelId(null); // Clear anchor if no user location/placements
      return;
    }

    // Set world origin ONCE on first valid user location
    if (!worldOriginGps) {
      setWorldOriginGps({ lat: userLocation.lat, lng: userLocation.lng });
      // Don't return yet, proceed to calculate placements if origin was just set
    }

    const currentNearby = placements.filter(p => {
      if (typeof p.latitude !== 'number' || typeof p.longitude !== 'number') return false;
      const dist = calculateHaversineDistance(userLocation.lat, userLocation.lng, p.latitude, p.longitude);
      return dist <= MAX_AR_DISTANCE;
    });
    setNearbyPlacements(currentNearby);

    // Find closest model for debug/info (optional, not for positioning)
    if (currentNearby.length > 0) {
      let closestDist = Infinity;
      let determinedAnchor: Placement | null = null;
      currentNearby.forEach(p => {
        const dist = calculateHaversineDistance(userLocation.lat, userLocation.lng, p.latitude, p.longitude);
        if (dist < closestDist) {
          closestDist = dist;
          determinedAnchor = p;
        }
      });
      if (determinedAnchor) {
        setAnchorModelId((determinedAnchor as Placement).id);
      } else {
        setAnchorModelId(null);
      }
    } else {
      setAnchorModelId(null);
    }

    if (!worldOriginGps) { // If origin couldn't be set (e.g. userLocation became null after check)
        setModelWorldTransforms({});
        return;
    }

    // Calculate absolute world transforms for all nearby models
    const newTransforms: { [id: number]: ModelTransform } = {};
    currentNearby.forEach(model => {
      // Calculate model's position relative to worldOriginGps
      const distanceToModelFromOrigin = calculateHaversineDistance(worldOriginGps.lat, worldOriginGps.lng, model.latitude, model.longitude);
      const bearingToModelFromOrigin = calculateBearing(worldOriginGps.lat, worldOriginGps.lng, model.latitude, model.longitude);

      // Convert bearing and distance from origin to world X, Z. North (+Z), East (+X)
      const modelWorldX = distanceToModelFromOrigin * Math.sin(toRadians(bearingToModelFromOrigin));
      const modelWorldZ = -distanceToModelFromOrigin * Math.cos(toRadians(bearingToModelFromOrigin)); // Negative Z for depth if North is +Z
      
      const modelInitialYRotation = parseFloat(model.rotation?.split(' ')[1] || '0');

      // Dynamic scaling (based on distance to USER, not origin)
      const distanceToModelFromUser = calculateHaversineDistance(userLocation.lat, userLocation.lng, model.latitude, model.longitude);
      let modelScaleMultiplier = REFERENCE_DISTANCE_FOR_SCALE / Math.max(0.1, distanceToModelFromUser);
      modelScaleMultiplier = Math.max(MIN_SCALE_MULTIPLIER, Math.min(MAX_SCALE_MULTIPLIER, modelScaleMultiplier));
      const baseModelScale = parseScaleString(model.scale);
      const finalModelScale = {
        x: baseModelScale.x * modelScaleMultiplier,
        y: baseModelScale.y * modelScaleMultiplier,
        z: baseModelScale.z * modelScaleMultiplier,
      };

      newTransforms[model.id] = {
        position: `${modelWorldX.toFixed(3)} ${MODEL_DEFAULT_Y} ${modelWorldZ.toFixed(3)}`,
        rotation: `0 ${modelInitialYRotation} 0`,
        scale: formatScaleToString(finalModelScale),
      };
    });
    setModelWorldTransforms(newTransforms);

  }, [userLocation, placements, worldOriginGps, MAX_AR_DISTANCE, MODEL_DEFAULT_Y, REFERENCE_DISTANCE_FOR_SCALE, MIN_SCALE_MULTIPLIER, MAX_SCALE_MULTIPLIER]);

  // useEffect to update camera position based on userLocation and worldOriginGps
  useEffect(() => {
    if (isSceneReady && cameraRef.current && userLocation && worldOriginGps) {
      const cameraEntity = cameraRef.current; // Corrected: cameraRef.current IS the A-Frame entity

      // Calculate camera's X, Z offset from worldOriginGps
      const distanceToUserFromOrigin = calculateHaversineDistance(worldOriginGps.lat, worldOriginGps.lng, userLocation.lat, userLocation.lng);
      const bearingToUserFromOrigin = calculateBearing(worldOriginGps.lat, worldOriginGps.lng, userLocation.lat, userLocation.lng);

      const cameraWorldX = distanceToUserFromOrigin * Math.sin(toRadians(bearingToUserFromOrigin));
      const cameraWorldZ = -distanceToUserFromOrigin * Math.cos(toRadians(bearingToUserFromOrigin)); // North is +Z
      
      const cameraY = 1.6; 

      cameraEntity.setAttribute('position', `${cameraWorldX.toFixed(3)} ${cameraY} ${cameraWorldZ.toFixed(3)}`);
    }
  }, [isSceneReady, cameraRef, userLocation, worldOriginGps]);

  const handleBack = useCallback(() => {
    navigate('/');
    window.location.reload();
  }, [navigate]);

  if (error) {
    return <div className={styles.errorContainer}><div className={styles.errorMessage}>{error} <button onClick={() => window.location.reload()}>Try Again</button> <button onClick={handleBack}>← Back to Map</button></div></div>;
  }

  const currentDisplayPlacements = nearbyPlacements; // Use the state updated by the new logic
  const totalModelsToLoad = currentDisplayPlacements?.length ?? 0;

  const calculatePlaneDimensions = () => {
      if (!videoAspectRatio) return { width: 16, height: 9 };
      const planeHeight = 10; 
      const planeWidth = planeHeight * videoAspectRatio;
      return { width: planeWidth, height: planeHeight };
  };
  const videoPlaneDimensions = calculatePlaneDimensions();

  return (
    <div className={styles.container}>
      {isSceneReady && (
        <a-scene
          ref={sceneRef}
          embedded
          renderer="logarithmicDepthBuffer: true; antialias: true; alpha: true; colorManagement: true;"
          vr-mode-ui="enabled: false"
          loading-screen="enabled: false"
          keyboard-shortcuts="enterVR: false; resetSensor: false"
          device-orientation-permission-ui="enabled: true"
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1 }}>
          
          {/* Camera setup for manual positioning and look controls */}
          <a-camera 
            ref={cameraRef} 
            look-controls="enabled: true; touchEnabled: true; magicWindowTrackingEnabled: true;"
            fov="80" 
            near="0.001"
          >
            {/* Video background plane */}
            {streamActive && videoRef.current && videoRef.current.id && videoAspectRatio && (
              <a-plane
                src={`#${videoRef.current.id}`}
                height={videoPlaneDimensions.height}
                width={videoPlaneDimensions.width}
                position="0 0 -10" // Adjust Z distance as needed, ensure it's behind content
                material="shader: flat; transparent: true; opacity: 1;" // Flat shader, ensure it's visible
              ></a-plane>
            )}
          </a-camera>

          {/* Map placements using manual transforms */}
          {currentDisplayPlacements.map(placement => {
            if (!modelWorldTransforms[placement.id]) return null;
            
            const transform = modelWorldTransforms[placement.id];
          const file = (placement.model_file ?? [])[0];
          if (!file?.url) return null;
          let fileUrl = file.url;
          if (!fileUrl.startsWith('http')) {
              fileUrl = `${getBackendBaseUrl()}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
          }

            return (
              <a-entity
                key={placement.id}
                gltf-model={fileUrl}
                position={transform.position}
                rotation={transform.rotation}
                scale={transform.scale}
                model-event-handler={`placementData: ${JSON.stringify(placement)}`}
                events={{ error: handleModelError }}
              />
            );
        })}

        <a-entity light="type: ambient; color: #BBB; intensity: 0.8"></a-entity>
        <a-entity light="type: directional; color: #FFF; intensity: 0.6;" position="-0.5 1 1"></a-entity>
      </a-scene>
    )}

    {/* Hidden video element - rendered only in debug mode */}
    {isDebugMode && (
      <video
        ref={videoRef}
        id="ar-video-feed"
        style={{
          position: 'fixed',
          top: '-9999px',
          left: '-9999px',
          display: 'none !important',
          width: '0px !important',
          height: '0px !important'
        }}
        autoPlay
        playsInline
        muted
      ></video>
    )}

    {/* UI Elements (Back button, loading overlay, instructions, debug info, no models message) */}
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 10, pointerEvents: 'none' }}>
      <button className={styles.backButton} onClick={handleBack} style={{ pointerEvents: 'auto' }}>← Back to Map</button>
    </div>
    <div className={styles.instructions} style={{ pointerEvents: 'none' }}>
      Walk around / drag screen to find couture.
    </div>
    {showDebugInfo && isDebugMode && (
      <div className={styles.debugInfo} style={{ pointerEvents: 'none' }}>
        <p>Mode: Manual AR</p>
        <p>Anchor ID: {anchorModelId ?? 'None'}</p>
        <p>Models loaded: {modelsLoaded}/{totalModelsToLoad}</p>
        <p>Nearby models: {currentDisplayPlacements?.length ?? 0}</p>
        <p>User location: {userLocation ? `${userLocation.lat.toFixed(6)}, ${userLocation.lng.toFixed(6)}` : 'Unknown'}</p>
        {locationError && <p style={{color: 'orange'}}>{locationError}</p>}
        {videoError && <p style={{color: 'red'}}>Camera Error: {videoError}</p>}
        <p>Video Aspect Ratio: {videoAspectRatio ? videoAspectRatio.toFixed(2) : 'N/A'}</p>
      </div>
    )}
     {currentDisplayPlacements?.length === 0 && placements.length > 0 && userLocation && (
      <div className={styles.noModelsMessage} style={{ pointerEvents: 'none' }}>
        <p>No AR content found within {MAX_AR_DISTANCE}m.</p>
        <button className={styles.backButton} onClick={handleBack} style={{ position: 'static', marginTop: '15px', pointerEvents: 'auto' }}>Return to Map</button>
      </div>
    )}
  </div>
);
};

export default ARViewer;
